import {allLogTypes as logTypes, defer} from '@servicenow/ui-internal';
import hostMap from './hostMap';
import log from '../../log';
import {timingMetricTypes, logTimingMetrics} from '@servicenow/ui-metrics';

function createDebounce(fn, {delay = 0, maxWait, collate = false}, host, ctx, runEffectFn) {
	let debounceVariables = getDebounceVariables();
	const validMaxWait = Number.isInteger(maxWait);
	const shouldCollate = collate === true;
	const tDelay = !Number.isInteger(delay) ? 0 : delay;
	const tMaxWait = validMaxWait ? maxWait : 0;

	const componentName = host?.tagName;
	logTimingMetrics(timingMetricTypes.DEBOUNCE, tDelay, componentName);

	if (validMaxWait && tMaxWait < tDelay) {
		log(
			'For the debounce modifier configurations, the property maxWait should be greater than the property delay.',
			{level: logTypes.WARN, host, origin: 'debounce'}
		);
	}

	const finishDebounce = fn => {
		invokeFn(shouldCollate, debounceVariables, runEffectFn, fn, ctx);
		debounceVariables = getDebounceVariables();
	};

	const collateData = coeffects => {
		const {
			action: {payload = {}}
		} = coeffects;
		debounceVariables.collated.push({action: {payload}});
	};

	return function debouncedEffect(...args) {
		clearTimeout(debounceVariables.delayId);
		debounceVariables.lastArgs = args;
		debounceVariables.saveEventProps();
		if (shouldCollate) collateData(args[args.length - 1]);
		debounceVariables.delayId = defer(() => finishDebounce(fn), tDelay);
		if (validMaxWait && tMaxWait && !debounceVariables.maxWaitId)
			debounceVariables.maxWaitId = defer(() => finishDebounce(fn), tMaxWait);
	};
}

const invokeFn = (accumulate, debounceVariables, runEffectFn, fn, ctx) => {
	debounceVariables.retrieveEventProps();
	const {collated, lastArgs} = debounceVariables;
	clearTimers(debounceVariables);
	if (accumulate) {
		const coeffects = lastArgs[lastArgs.length - 1];
		coeffects.collated = collated;
	}
	runEffectFn(fn, lastArgs, ctx);
};

const clearTimers = ({delayId, maxWaitId}) => {
	clearTimeout(delayId);
	clearTimeout(maxWaitId);
};

const getDebounceVariables = () => ({
	delayId: null,
	maxWaitId: null,
	lastArgs: null,
	collated: [],
	event: {
		eventPath: [],
		currentTarget: null
	},
	saveEventProps() {
		const coeffects = this.lastArgs[this.lastArgs.length - 1];
		const event = coeffects.action?.payload.event;
		this.event.eventPath = event?.composedPath();
		this.event.currentTarget = event?.currentTarget;
	},
	retrieveEventProps() {
		const coeffects = this.lastArgs[this.lastArgs.length - 1];
		const event = coeffects.action?.payload.event;
		if (typeof event === 'undefined') return;
		event.composedPath = () => this.event.eventPath;
		Object.defineProperty(event, 'currentTarget', {
			value: this.event.currentTarget,
			writable: false
		});
	}
});

/**
 * Debounce modifier for effect functions
 *
 * @param host
 * @param actionType
 * @param effectFn
 * @param modifier
 */
export default function debounce(host, actionType, effectFn, modifier, ctx, runEffectFn) {
	if (!hostMap.has(host)) hostMap.set(host, new WeakMap());

	const effectMap = hostMap.get(host);

	if (!effectMap.has(effectFn)) effectMap.set(effectFn, {});

	const actionMappings = effectMap.get(effectFn);
	if (!actionMappings[actionType])
		actionMappings[actionType] = createDebounce(effectFn, modifier, host, ctx, runEffectFn);

	return actionMappings[actionType];
}
